import { useRef } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { cn } from "@wags/ui/ui-utils";
import { VercelImage } from "@wags/ui/vercel-image.tsx";
import type { ListingPage } from "@wags/catalog/listings/listing-page.contract.ts";
import { formatDistance } from "@wags/catalog/pure.ts";
import { isVideoMedia, normalizeRemoteMediaUrl } from "@wags/utils/media.ts";
import WagsLogo from "@/assets/icons/wags-logo.svg";
import { PhotoLoadingIndicator } from "./photo-loading-indicator";
import { REVEAL_IMAGE_PROPS } from "./dog-card-photo-reveal";
import {
    ListingCardFavorite,
    useListingCardPresentation,
} from "./listing-card-provider";
import { DOG_CARD_PRESS_CLASS } from "@/components/shared/dogCardGrid";
import { useMediaQuery } from "@wags/tanstack-start/react";
import {
    ADOPTION_STATUS_LABEL,
    type AdoptionStatus,
    type ChangedAdoptionStatus,
    changedAdoptionStatus,
} from "./adoption-status";
import { StatusSticker } from "./status-sticker";
import { formatMoneyRange } from "@/lib/ui/price";
import {
    dogCardHistoryKey,
    nextDogCardHistoryState,
    shouldCommitDogCardHistory,
    windowHistoryIsRouterOwned,
} from "./dog-card-history";

const IMAGE_ZOOM_CLASS =
    "transition-transform duration-150 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none [@media(hover:hover)]:group-hover/card:scale-104 [@media(hover:hover)]:group-hover/card:duration-200 pointer-events-none select-none";

type PriceCaption = { label: string; listed: boolean };

function getPriceCaption(
    priceRange: OverlayDogCardProps["priceRange"],
    changed: ChangedAdoptionStatus | null,
): PriceCaption {
    if (changed)
        return { label: ADOPTION_STATUS_LABEL[changed], listed: false };
    const label = formatMoneyRange(priceRange);
    return label
        ? { label, listed: true }
        : { label: "Price not listed", listed: false };
}

type DogCardBaseProps = {
    id: string;
    name: string;
    imageUrl: string;
    breedNames: string[];
    onOpen?: (selectionId?: string) => void;
};

type ExploreDogCardProps = DogCardBaseProps & {
    variant: "explore";
    onTouchActivate?: (id: string, triggerEl: HTMLElement) => void;
    ageLabel?: string;
    distanceMiles?: number | null;
};

type OverlayDogCardProps = DogCardBaseProps & {
    variant: "overlay";
    status?: AdoptionStatus;
    distanceMiles?: number | null;
    priceRange?: NonNullable<ListingPage["listing"]["price"]> | null;
    litter?: boolean;
    showUndoToast?: boolean;
    favoriteVisibility?: "available" | "always";
    presentationId?: string;
    measurementSurface?: string;
    priority?: boolean;
    commitHistoryFirst?: boolean;
};

type DogCardProps = ExploreDogCardProps | OverlayDogCardProps;

function ExploreDogCard({
    id,
    name,
    imageUrl,
    breedNames,
    ageLabel,
    distanceMiles,
    onOpen,
    onTouchActivate,
}: ExploreDogCardProps) {
    const isTouchDevice = useMediaQuery("(hover: none)");
    return (
        <Link
            to="/dog/$dogId"
            params={{ dogId: id }}
            data-doomscroll-trigger-id={id}
            className="group/card relative w-full h-full overflow-hidden rounded-sm transition-transform duration-200 ease-[cubic-bezier(0.25,1,0.5,1)] active:scale-98 active:duration-100 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow focus-visible:ring-offset-2"
            onClick={(e) => {
                onOpen?.();
                if (onTouchActivate && isTouchDevice) {
                    e.preventDefault();
                    onTouchActivate(id, e.currentTarget);
                }
            }}
        >
            <div className="relative w-full h-full rounded-sm overflow-hidden img-inset">
                {imageUrl ? (
                    <VercelImage
                        src={imageUrl}
                        listingMedia
                        alt={`${name} - ${breedNames.join(" / ") || "Unknown breed"}`}
                        width={400}
                        height={400}
                        className={`w-full h-full object-cover ${IMAGE_ZOOM_CLASS}`}
                        videoMode="thumbnail"
                    />
                ) : (
                    <div className="w-full h-full bg-havanese flex items-center justify-center">
                        <WagsLogo className="h-8 w-auto text-doberman-black/10" />
                    </div>
                )}
            </div>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-true-black/40 to-transparent pt-6 pb-2 px-2.5">
                <p className="text-item-title text-true-white truncate">
                    {name}
                </p>
                {(ageLabel || distanceMiles != null) && (
                    <p className="text-caption text-true-white/70 truncate mt-0.5">
                        {[ageLabel, formatDistance(distanceMiles, "compact")]
                            .filter(Boolean)
                            .join(" · ")}
                    </p>
                )}
            </div>
        </Link>
    );
}

function OverlayPhoto({
    src,
    alt,
    width,
    height,
    priority,
}: {
    src: string;
    alt: string;
    width: number;
    height: number;
    priority?: boolean;
}) {
    const photoClassName = `absolute inset-0 h-full w-full object-cover ${IMAGE_ZOOM_CLASS}`;
    if (isVideoMedia({ url: normalizeRemoteMediaUrl(src) ?? src })) {
        return (
            <VercelImage
                src={src}
                listingMedia
                alt={alt}
                width={width}
                height={height}
                priority={priority}
                className={photoClassName}
                data-card-media
            />
        );
    }
    return (
        <>
            <VercelImage
                src={src}
                listingMedia
                alt={alt}
                width={width}
                height={height}
                priority={priority}
                {...REVEAL_IMAGE_PROPS}
                className={`peer ${photoClassName}`}
                data-card-media
            />
            <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 flex items-center justify-center bg-photo-veil peer-data-revealed:hidden"
            >
                <PhotoLoadingIndicator />
            </span>
        </>
    );
}

function OverlayCaption({
    name,
    breedNames,
    priceCaption,
    distanceLabel,
}: {
    name: string;
    breedNames: string[];
    priceCaption: PriceCaption;
    distanceLabel: string | null;
}) {
    return (
        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-px px-4 pb-3 @[380px]:gap-0.75 @[380px]:px-5 @[380px]:pb-4">
            <p className="text-item-title text-true-white truncate">{name}</p>
            {breedNames.length > 0 && (
                <p className="text-body-small truncate text-true-white">
                    {breedNames.join(" / ")}
                </p>
            )}
            <p className="mt-0.5 flex h-lh min-w-0 flex-wrap gap-x-1 overflow-hidden text-body-small">
                <span
                    className={cn(
                        "max-w-full truncate",
                        priceCaption.listed
                            ? "font-semibold tabular-nums text-true-white/95"
                            : "text-true-white/75",
                    )}
                >
                    {priceCaption.label}
                </span>
                {distanceLabel && (
                    <span className="whitespace-nowrap tabular-nums text-true-white/75">
                        · {distanceLabel}
                    </span>
                )}
            </p>
        </div>
    );
}

function OverlayDogCard({
    id,
    name,
    imageUrl,
    breedNames,
    status,
    distanceMiles,
    priceRange,
    litter,
    showUndoToast,
    favoriteVisibility,
    onOpen,
    presentationId: attributedPresentationId,
    measurementSurface,
    priority,
    commitHistoryFirst = false,
}: OverlayDogCardProps) {
    const localPresentationId = useRef(crypto.randomUUID());
    const presentation = useListingCardPresentation();
    const presentationId =
        attributedPresentationId ?? localPresentationId.current;
    const router = useRouter();
    const changed = changedAdoptionStatus(status);
    return (
        <Link
            to="/dog/$dogId"
            params={{ dogId: id }}
            data-doomscroll-trigger-id={id}
            className={`group/card @container w-full flex flex-col has-[[data-save-button]:is(:hover,:active)]:[&_[data-card-media]]:scale-100 ${DOG_CARD_PRESS_CLASS}`}
            onClick={(e) => {
                const selectionId = crypto.randomUUID();
                onOpen?.(selectionId);
                presentation.onCardOpened?.(
                    id,
                    presentationId,
                    selectionId,
                    measurementSurface,
                );
                if (!commitHistoryFirst || !shouldCommitDogCardHistory(e)) {
                    return;
                }
                e.preventDefault();
                const built = router.buildLocation({
                    to: "/dog/$dogId",
                    params: { dogId: id },
                });
                const state = nextDogCardHistoryState(
                    router.history.location.state,
                    dogCardHistoryKey(),
                );
                const href = router.history.createHref(built.publicHref);
                if (windowHistoryIsRouterOwned(window.history.pushState)) {
                    window.history.pushState(state, "", href);
                    return;
                }
                router.history.push(built.publicHref, state);
            }}
        >
            <div
                data-card-photo
                className="relative w-full aspect-4/5 rounded-card overflow-hidden img-inset"
            >
                {imageUrl ? (
                    <OverlayPhoto
                        key={imageUrl}
                        src={imageUrl}
                        alt={`${name} - ${breedNames.join(" / ") || "Unknown breed"}`}
                        width={220}
                        height={275}
                        priority={priority}
                    />
                ) : (
                    <div className="w-full h-full bg-havanese flex items-center justify-center">
                        <WagsLogo className="h-8 w-auto text-doberman-black/10" />
                    </div>
                )}
                <div
                    skullet-ignore=""
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-doberman-black/70 mask-ease-to-t"
                />
                {!changed && litter && (
                    <div className="absolute top-2.5 left-3 flex h-8 items-center">
                        <span className="rounded-label bg-doberman-black/60 px-2.5 py-1 text-caption font-semibold text-true-white backdrop-blur-sm">
                            Litter
                        </span>
                    </div>
                )}
                {changed && (
                    <StatusSticker
                        id={id}
                        status={changed}
                        className="absolute top-3 left-3"
                    />
                )}
                {(!changed || favoriteVisibility === "always") && (
                    <ListingCardFavorite
                        listingId={id}
                        showUndoToast={showUndoToast}
                    />
                )}
                <OverlayCaption
                    name={name}
                    breedNames={breedNames}
                    priceCaption={getPriceCaption(priceRange, changed)}
                    distanceLabel={formatDistance(distanceMiles, "compact")}
                />
            </div>
        </Link>
    );
}

export function DogCard(props: DogCardProps) {
    if (props.variant === "explore") {
        return <ExploreDogCard {...props} />;
    }
    return <OverlayDogCard {...props} />;
}
